import { ChangeEvent, FormEvent, useEffect, useReducer, useState } from "react";
import { ProductVariant } from "../../../services/types/productVariant.type";
import {
  useCreateProductVariantMutation,
  useDeleteProductVariantMutation,
  useGetProductVariantsQuery,
  useUpdateProductVariantMutation,
} from "../../../store/productVariants/productVariantsApi";
import { TextButton } from "../../ui/buttons/TextButton/TextButton";
import { TextInput } from "../../ui/inputs/TextInput/TextInput";
import { ButtonWrap } from "../../ui/wrappers/ButtonWrap/ButtonWrap";
import ModalWrap from "../../ui/wrappers/ModalWrap/ModalWrap";
import "./StockModal.css";
import { NumberInput } from "../../ui/inputs/NumberInput/NumberInput";
import arrowUp from "../../../assets/icons/arrow-up.png";
import arrowDown from "../../../assets/icons/arrow.png";
import cross from "../../../assets/icons/close.png";
import { IconButton } from "../../ui/buttons/IconButton/IconButton";
import LoadBlock from "../LoadBlock/LoadBlock";
import { TextError } from "../../ui/errors/TextError/TextError";

interface VariantsState {
  variants: ProductVariant[];
  deletedIds: string[];
}

type VariantAction =
  | { type: "INITIALIZE"; payload: ProductVariant[] }
  | { type: "ADD"; payload: ProductVariant }
  | {
      type: "UPDATE";
      index: number;
      field: keyof Omit<ProductVariant, "position" | "uuid">;
      value: string | number;
    }
  | { type: "MOVE"; index: number; direction: "up" | "down" }
  | { type: "DELETE"; index: number };

const updatePositions = (variants: ProductVariant[]): ProductVariant[] =>
  variants.map((variant, idx) => ({ ...variant, position: idx }));

const variantsReducer = (
  state: VariantsState,
  action: VariantAction,
): VariantsState => {
  switch (action.type) {
    case "INITIALIZE":
      return { variants: updatePositions(action.payload), deletedIds: [] };
    case "ADD": {
      const newVariant = { ...action.payload, position: state.variants.length };
      return { ...state, variants: [...state.variants, newVariant] };
    }
    case "UPDATE": {
      const updated = state.variants.map((variant, idx) =>
        idx === action.index
          ? { ...variant, [action.field]: action.value }
          : variant,
      );
      return { ...state, variants: updated };
    }
    case "MOVE": {
      const updated = [...state.variants];
      const newIndex =
        action.direction === "up" ? action.index - 1 : action.index + 1;
      if (newIndex < 0 || newIndex >= updated.length) return state;
      [updated[action.index], updated[newIndex]] = [
        updated[newIndex],
        updated[action.index],
      ];
      return { ...state, variants: updatePositions(updated) };
    }
    case "DELETE": {
      const variantToDelete = state.variants[action.index];
      const updated = state.variants.filter((_, idx) => idx !== action.index);
      const newDeletedIds = variantToDelete.uuid
        ? [...state.deletedIds, variantToDelete.uuid]
        : state.deletedIds;
      return { variants: updatePositions(updated), deletedIds: newDeletedIds };
    }
    default:
      return state;
  }
};

interface StockModalProps {
  productUuid: string;
  productName?: string;
  closeModal: () => void;
  updatePage: () => void;
}

export const StockModal = ({
  productUuid,
  productName,
  closeModal,
  updatePage,
}: StockModalProps) => {
  const {
    data: fetchedVariants,
    isLoading: isVariantsLoad,
    error: variantError,
    refetch,
  } = useGetProductVariantsQuery(
    { productUuid },
    { refetchOnMountOrArgChange: true },
  );

  const [state, dispatch] = useReducer(variantsReducer, {
    variants: [],
    deletedIds: [],
  });
  const [localError, setLocalError] = useState<string>("");

  // Инициализируем состояние редьюсера при получении данных с сервера
  useEffect(() => {
    if (fetchedVariants) {
      dispatch({ type: "INITIALIZE", payload: fetchedVariants });
    }
  }, [fetchedVariants]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const { name, value } = e.target;
    dispatch({
      type: "UPDATE",
      index,
      field: name as keyof Omit<ProductVariant, "position" | "uuid">,
      value: name === "stock" ? (value === "" ? "" : Number(value)) : value,
    });
  };

  const handleMove = (index: number, direction: "up" | "down") => {
    dispatch({ type: "MOVE", index, direction });
  };

  const handleDelete = (index: number) => {
    dispatch({ type: "DELETE", index });
  };

  const handleAddVariant = () => {
    dispatch({
      type: "ADD",
      payload: { size: "", stock: 0, position: state.variants.length },
    });
  };

  const [createVariant] = useCreateProductVariantMutation();
  const [updateVariant] = useUpdateProductVariantMutation();
  const [deleteVariantApi] = useDeleteProductVariantMutation();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // Базовая валидация: размеры и количество должны быть заполнены
    for (const variant of state.variants) {
      if (!variant.size) {
        setLocalError("Каждый вариант должен иметь указанный размер.");
        return;
      }
      if (variant.stock < 0) {
        setLocalError(
          "Количество должно быть указано и не быть отрицательным.",
        );
        return;
      }
    }
    setLocalError("");
    try {
      // Обработка новых вариантов (без uuid)
      const newVariants = state.variants.filter((v) => !v.uuid);
      const createPromises = newVariants.map((variant) =>
        createVariant({
          productUuid,
          body: {
            size: variant.size,
            stock: variant.stock,
            position: variant.position,
          },
        }).unwrap(),
      );
      // Обработка обновления существующих вариантов
      const existingVariants = state.variants.filter((v) => v.uuid);
      const updatePromises = existingVariants.map((variant) =>
        updateVariant({
          productUuid,
          variantUuid: variant.uuid!,
          body: {
            size: variant.size,
            stock: variant.stock,
            position: variant.position,
          },
        }).unwrap(),
      );
      // Обработка удаления вариантов
      const deletePromises = state.deletedIds.map((variantUuid) =>
        deleteVariantApi({ productUuid, variantUuid }).unwrap(),
      );

      await Promise.all([
        ...createPromises,
        ...updatePromises,
        ...deletePromises,
      ]);
      updatePage();
      closeModal();
    } catch (error) {
      console.error("Ошибка сохранения вариантов", error);
      setLocalError("Ошибка сохранения вариантов");
    }
  };

  const handleClose = () => {
    updatePage();
    closeModal();
  };

  return (
    <ModalWrap
      title={`Редактирование позиций "${productName}"`}
      action={handleClose}
    >
      {isVariantsLoad ? (
        <LoadBlock />
      ) : (
        <form className="stock-table" onSubmit={handleSubmit}>
          <div className="stock-table-row">
            <div></div>
            <div className="stock-table-title">Название</div>
            <div className="stock-table-title">Количество</div>
          </div>
          {state.variants.map((variant, index) => (
            <div className="stock-table-row" key={index}>
              <div className="stock-table-arrows">
                <IconButton
                  width="100%"
                  icon={arrowUp}
                  action={() => handleMove(index, "up")}
                />
                <IconButton
                  width="100%"
                  icon={arrowDown}
                  action={() => handleMove(index, "down")}
                />
              </div>
              <TextInput
                name="size"
                width="auto"
                value={variant.size}
                action={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(e, index)
                }
              />
              <NumberInput
                name="stock"
                width="auto"
                value={variant.stock}
                action={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(e, index)
                }
              />
              <IconButton
                width="100%"
                icon={cross}
                action={() => handleDelete(index)}
              />
            </div>
          ))}
          <TextButton
            action={handleAddVariant}
            text="Добавить вариант"
            textDecoration="underline"
          />
          <ButtonWrap>
            <TextButton
              type="submit"
              text="Сохранить"
              color="black"
              margin="2rem 0 0 0"
            />
            <TextButton
              type="button"
              text="Отмена"
              color="white"
              margin="2rem 0 0 0"
              action={handleClose}
            />
          </ButtonWrap>
        </form>
      )}
      <TextError text={JSON.stringify(variantError)} />
    </ModalWrap>
  );
};
